import { AREA_KIND_LABELS, areaDefinition, nextInstanceName, type AreaGroup, type AreaKind } from './areaCatalog';
import type { BasementFinish, BasementType, PropertyType, V2Area, AreaNameLabel } from './inspection';

// House plan — the quick field configurator behind 新建验屋 (楼层 × 房间, 附属空间, 系统) and
// the vocabulary of 添加区域 afterwards.
//
// It is a UI-side description of the house that is turned into ordinary V2Area records at
// creation time. It is NOT persisted and NOT a second area authority (task book §5): after
// creation the only source of truth is the Area records themselves, and the list screen
// derives the floor structure back from Area.floor (areaSectionOf below).
//
// Every preset maps onto an existing AreaKind — the checklist template selector — and only
// chooses the instance NAME and FLOOR. Checklist content is untouched: a 爬行空间 walks the
// basement checklist, a 独立车库 the garage checklist, a 洗衣房 / 书房 the interior checklist.
//
// Owner task book 2026-09-04 Round 2 §D: a NORMAL new inspection starts from the EMPTY plan
// (emptyHousePlan) — no floors, no rooms, no basement, no attic, no garage. The property-type
// suggestion (defaultHousePlan) exists only behind an explicit inspector action ("Use suggested
// layout", D-04); it is never applied silently.

export type RoomKey = 'kitchen' | 'living' | 'family' | 'dining' | 'bedroom' | 'fullBath' | 'halfBath' | 'laundry';
// NY F1 Round 3 (AG-01 product half, frozen Plan §21 Round 3): a carport is a garage-checklist space
// alongside the detached garage; ordinary sheds / outbuildings stay separate ("other").
export type SpaceKey = 'garage' | 'detachedGarage' | 'carport' | 'basement' | 'crawlspace' | 'attic';
export type SystemKey = 'exterior' | 'roof' | 'structure' | 'electrical' | 'plumbing' | 'waterHeater' | 'hvac' | 'safety';

export interface AreaPreset<K extends string = string> {
  key: K;
  label: string;
  kind: AreaKind;
  // Instance name stem; numbered when more than one instance exists ("卧室 1", "卧室 2").
  base: string;
}

export const ROOM_PRESETS: readonly AreaPreset<RoomKey>[] = Object.freeze([
  { key: 'kitchen', label: '厨房', kind: 'kitchen', base: '厨房' },
  { key: 'living', label: '客厅', kind: 'interior', base: '客厅' },
  { key: 'family', label: '家庭房', kind: 'interior', base: '家庭房' },
  { key: 'dining', label: '餐厅', kind: 'interior', base: '餐厅' },
  { key: 'bedroom', label: '卧室', kind: 'bedroom', base: '卧室' },
  { key: 'fullBath', label: '全卫', kind: 'bathroom', base: '卫生间' },
  { key: 'halfBath', label: '半卫', kind: 'bathroom', base: '半卫' },
  { key: 'laundry', label: '洗衣房', kind: 'interior', base: '洗衣房' }
]);

export interface SpacePreset extends AreaPreset<SpaceKey> {
  // Counted spaces take a number (two garages); the rest are present / absent.
  counted: boolean;
}

export const SPACE_PRESETS: readonly SpacePreset[] = Object.freeze([
  { key: 'garage', label: '车库', kind: 'garage', base: '车库', counted: true },
  { key: 'detachedGarage', label: '独立车库', kind: 'garage', base: '独立车库', counted: true },
  { key: 'carport', label: '车棚', kind: 'garage', base: '车棚', counted: true },
  { key: 'basement', label: '地下室', kind: 'basement', base: '地下室', counted: false },
  { key: 'crawlspace', label: '爬行空间', kind: 'basement', base: '爬行空间', counted: false },
  { key: 'attic', label: '阁楼', kind: 'attic', base: '阁楼', counted: false }
]);

const SYSTEM_KEYS: readonly SystemKey[] = Object.freeze(['exterior', 'roof', 'structure', 'electrical', 'plumbing', 'waterHeater', 'hvac', 'safety']);

export const SYSTEM_PRESETS: readonly AreaPreset<SystemKey>[] = Object.freeze(
  SYSTEM_KEYS.map((key) => ({ key, label: AREA_KIND_LABELS[key], kind: key, base: AREA_KIND_LABELS[key] }))
);

export const OTHER_PRESET: AreaPreset<'other'> = Object.freeze({ key: 'other', label: '其他区域', kind: 'other', base: '其他区域' });

// Everything the inspector can add after creation, with whether it belongs to a floor.
export interface AddablePreset extends AreaPreset {
  floorBound: boolean;
}

export const ADDABLE_PRESETS: readonly AddablePreset[] = Object.freeze([
  ...ROOM_PRESETS.map((p) => ({ ...p, floorBound: true })),
  ...SPACE_PRESETS.map((p) => ({ key: p.key, label: p.label, kind: p.kind, base: p.base, floorBound: false })),
  ...SYSTEM_PRESETS.map((p) => ({ ...p, floorBound: false })),
  { ...OTHER_PRESET, floorBound: true }
]);

export interface FloorPlan {
  rooms: Record<RoomKey, number>;
  // Named extra rooms on this floor (书房, 阳光房, 设备间) — interior checklist.
  extras: string[];
}

// Basement Details (Owner task book §7): the space TYPE and the finish STATE are two separate
// choices, both UNKNOWN (null) until the inspector sets them. The type drives which space is
// seeded (basement / crawlspace / none); both facts are stored on the inspection's property
// record for the report.
export interface BasementDetails {
  type: BasementType | null;
  finish: BasementFinish | null;
}

export interface HousePlan {
  floors: FloorPlan[];
  spaces: Record<SpaceKey, number>;
  systems: Record<SystemKey, boolean>;
  basement: BasementDetails;
  // Custom named areas that are not on a floor (workshop, shed, pool house) — "other" checklist.
  others: string[];
}

export const MAX_FLOORS = 8;
export const MAX_INSTANCES = 9;

const EMPTY_ROOMS: Record<RoomKey, number> = Object.freeze({
  kitchen: 0,
  living: 0,
  family: 0,
  dining: 0,
  bedroom: 0,
  fullBath: 0,
  halfBath: 0,
  laundry: 0
}) as Record<RoomKey, number>;

function floorWith(partial: Partial<Record<RoomKey, number>>): FloorPlan {
  return { rooms: { ...EMPTY_ROOMS, ...partial }, extras: [] };
}

function spacesWith(partial: Partial<Record<SpaceKey, number>>): Record<SpaceKey, number> {
  return { garage: 0, detachedGarage: 0, carport: 0, basement: 0, crawlspace: 0, attic: 0, ...partial };
}

function systemsWith(overrides: Partial<Record<SystemKey, boolean>> = {}): Record<SystemKey, boolean> {
  return { exterior: true, roof: true, structure: true, electrical: true, plumbing: true, waterHeater: true, hvac: true, safety: true, ...overrides };
}

function basementWith(type: BasementType | null, finish: BasementFinish | null = null): BasementDetails {
  return { type, finish };
}

// Space counts that follow a basement type: full / partial → one basement area, crawlspace → one
// crawlspace area, slab / none / unknown → neither.
export function spacesForBasement(type: BasementType | null): Pick<Record<SpaceKey, number>, 'basement' | 'crawlspace'> {
  return { basement: type === 'full' || type === 'partial' ? 1 : 0, crawlspace: type === 'crawlspace' ? 1 : 0 };
}

export function withBasementType(plan: HousePlan, type: BasementType | null): HousePlan {
  if (plan.basement.type === type) return plan;
  return { ...plan, basement: { ...plan.basement, type }, spaces: { ...plan.spaces, ...spacesForBasement(type) } };
}

export function withBasementFinish(plan: HousePlan, finish: BasementFinish | null): HousePlan {
  if (plan.basement.finish === finish) return plan;
  return { ...plan, basement: { ...plan.basement, finish } };
}

export function emptyFloor(): FloorPlan {
  return floorWith({});
}

// What an added upper floor usually is: bedrooms and a bathroom. Only used by the explicit
// property-type suggestion — never by "+ Floor" (Round 2 §D: nothing is seeded silently).
export function suggestedUpperFloor(): FloorPlan {
  return floorWith({ bedroom: 2, fullBath: 1 });
}

export function floorLabel(floor: number): string {
  const names = ['一楼', '二楼', '三楼', '四楼', '五楼', '六楼', '七楼', '八楼'];
  return names[floor - 1] ?? `${floor} 楼`;
}

// The starting point of every normal new inspection: nothing about the house is known yet. The
// whole-house systems are inspection coverage, not property facts, so they stay on.
export function emptyHousePlan(): HousePlan {
  return { floors: [], spaces: spacesWith({}), systems: systemsWith(), basement: basementWith(null), others: [] };
}

// True when the plan carries no property facts at all (what a fresh form shows as 未设置).
export function planIsEmpty(plan: HousePlan): boolean {
  return plan.floors.length === 0 && Object.values(plan.spaces).every((n) => n === 0) && plan.basement.type === null && plan.basement.finish === null && plan.others.length === 0;
}

// Quick-mode suggestion per property type (task book §7; D-04 Suggested Layout). Applied ONLY by an
// explicit inspector action; every count is editable afterwards.
export function defaultHousePlan(propertyType: PropertyType): HousePlan {
  switch (propertyType) {
    case 'singleFamily':
      return {
        floors: [floorWith({ kitchen: 1, living: 1, dining: 1, halfBath: 1 }), floorWith({ bedroom: 3, fullBath: 2 })],
        spaces: spacesWith({ garage: 1, basement: 1, attic: 1 }),
        systems: systemsWith(),
        basement: basementWith('full'),
        others: []
      };
    case 'townhouse':
      return {
        floors: [floorWith({ kitchen: 1, living: 1, halfBath: 1 }), floorWith({ bedroom: 2, fullBath: 1 })],
        spaces: spacesWith({ garage: 1, basement: 1 }),
        systems: systemsWith(),
        basement: basementWith('full'),
        others: []
      };
    case 'multiFamily':
      return {
        floors: [floorWith({ kitchen: 1, living: 1, bedroom: 2, fullBath: 1 }), floorWith({ kitchen: 1, living: 1, bedroom: 2, fullBath: 1 })],
        spaces: spacesWith({ basement: 1 }),
        systems: systemsWith(),
        basement: basementWith('full'),
        others: []
      };
    case 'condo':
      return {
        floors: [floorWith({ kitchen: 1, living: 1, bedroom: 2, fullBath: 1 })],
        spaces: spacesWith({}),
        systems: systemsWith({ roof: false }),
        basement: basementWith('none'),
        others: []
      };
    case 'other':
      return {
        floors: [floorWith({ kitchen: 1, living: 1, bedroom: 2, fullBath: 1 })],
        spaces: spacesWith({ garage: 1, basement: 1, attic: 1 }),
        systems: systemsWith(),
        basement: basementWith('full'),
        others: []
      };
  }
}

// ---------- immutable plan edits ----------

function clampCount(value: number, max: number = MAX_INSTANCES): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(max, Math.round(value)));
}

// Floors added here start EMPTY (Round 2 §D); the inspector sets the rooms per floor.
export function withFloorCount(plan: HousePlan, count: number): HousePlan {
  const target = Math.max(0, Math.min(MAX_FLOORS, Math.round(count)));
  if (target === plan.floors.length) return plan;
  if (target < plan.floors.length) return { ...plan, floors: plan.floors.slice(0, target) };
  const floors = [...plan.floors];
  while (floors.length < target) floors.push(emptyFloor());
  return { ...plan, floors };
}

export function withAddedFloor(plan: HousePlan): HousePlan {
  return withFloorCount(plan, plan.floors.length + 1);
}

export function withoutFloor(plan: HousePlan, floorIndex: number): HousePlan {
  if (floorIndex < 0 || floorIndex >= plan.floors.length) return plan;
  return { ...plan, floors: plan.floors.filter((_, i) => i !== floorIndex) };
}

export function withRoomCount(plan: HousePlan, floorIndex: number, key: RoomKey, count: number): HousePlan {
  const floor = plan.floors[floorIndex];
  if (!floor) return plan;
  const next = clampCount(count);
  if (floor.rooms[key] === next) return plan;
  const floors = plan.floors.map((f, i) => (i === floorIndex ? { ...f, rooms: { ...f.rooms, [key]: next } } : f));
  return { ...plan, floors };
}

export function withExtraRoom(plan: HousePlan, floorIndex: number, name: string): HousePlan {
  const floor = plan.floors[floorIndex];
  const trimmed = name.trim();
  if (!floor || trimmed.length === 0) return plan;
  const floors = plan.floors.map((f, i) => (i === floorIndex ? { ...f, extras: [...f.extras, trimmed] } : f));
  return { ...plan, floors };
}

export function withoutExtraRoom(plan: HousePlan, floorIndex: number, extraIndex: number): HousePlan {
  const floor = plan.floors[floorIndex];
  if (!floor || extraIndex < 0 || extraIndex >= floor.extras.length) return plan;
  const floors = plan.floors.map((f, i) => (i === floorIndex ? { ...f, extras: f.extras.filter((_, j) => j !== extraIndex) } : f));
  return { ...plan, floors };
}

export function withSpaceCount(plan: HousePlan, key: SpaceKey, count: number): HousePlan {
  const preset = SPACE_PRESETS.find((p) => p.key === key);
  if (!preset) return plan;
  const next = clampCount(count, preset.counted ? MAX_INSTANCES : 1);
  if (plan.spaces[key] === next) return plan;
  return { ...plan, spaces: { ...plan.spaces, [key]: next } };
}

export function withSystem(plan: HousePlan, key: SystemKey, enabled: boolean): HousePlan {
  if (plan.systems[key] === enabled) return plan;
  return { ...plan, systems: { ...plan.systems, [key]: enabled } };
}

export function withOtherArea(plan: HousePlan, name: string): HousePlan {
  const trimmed = name.trim();
  if (trimmed.length === 0) return plan;
  return { ...plan, others: [...plan.others, trimmed] };
}

export function withoutOtherArea(plan: HousePlan, index: number): HousePlan {
  if (index < 0 || index >= plan.others.length) return plan;
  return { ...plan, others: plan.others.filter((_, i) => i !== index) };
}

// ---------- plan → area seeds ----------

export interface AreaSeed {
  kind: AreaKind;
  name: string;
  floor: number | null;
  // Language-neutral name descriptor (null for inspector-named extras) — see AreaNameLabel.
  label: AreaNameLabel | null;
}

// Instance NAMING for the seeds — the only language-dependent part of the plan. Area names are
// inspector data (renamable, printed on the report), so they are generated ONCE in the App Language
// in force at creation; the plan structure itself is language-neutral. The default naming is the
// Chinese preset vocabulary above (owner-tested on device); src/i18n/labels.ts builds the en-US one.
export interface PlanNaming {
  roomBase: Record<RoomKey, string>;
  spaceBase: Record<SpaceKey, string>;
  systemName: Record<SystemKey, string>;
  // Prefix put in front of a room name when the house has more than one floor ("二楼", "Second Floor ").
  floorPrefix: (floor: number) => string;
}

export const DEFAULT_PLAN_NAMING: PlanNaming = Object.freeze({
  roomBase: Object.fromEntries(ROOM_PRESETS.map((p) => [p.key, p.base])) as Record<RoomKey, string>,
  spaceBase: Object.fromEntries(SPACE_PRESETS.map((p) => [p.key, p.base])) as Record<SpaceKey, string>,
  systemName: Object.fromEntries(SYSTEM_PRESETS.map((p) => [p.key, p.base])) as Record<SystemKey, string>,
  floorPrefix: floorLabel
});

// "卧室" for a single instance; "卧室 1", "卧室 2", … when there are several.
export function instanceNames(base: string, count: number): string[] {
  if (count <= 0) return [];
  if (count === 1) return [base];
  return Array.from({ length: count }, (_, i) => `${base} ${i + 1}`);
}

// Walk order: outside → garages → basement / crawlspace → systems → each floor (rooms in
// preset order, then named extras) → custom areas → attic → whole-house safety review. Room names
// carry the floor when the house has more than one, so the area title alone tells the inspector
// where they are ("二楼卧室 2"); the floor itself is stored on the record (Area.floor).
export function areaSeedsFromPlan(plan: HousePlan, naming: PlanNaming = DEFAULT_PLAN_NAMING): AreaSeed[] {
  const out: AreaSeed[] = [];
  const system = (key: SystemKey) => {
    if (plan.systems[key]) out.push({ kind: key, name: naming.systemName[key], floor: null, label: { preset: key, index: null, withFloor: false } });
  };
  const space = (key: SpaceKey) => {
    const preset = SPACE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    instanceNames(naming.spaceBase[key], plan.spaces[key]).forEach((name, i, all) => {
      out.push({ kind: preset.kind, name, floor: null, label: { preset: key, index: all.length > 1 ? i + 1 : null, withFloor: false } });
    });
  };
  system('exterior');
  system('roof');
  space('garage');
  space('detachedGarage');
  space('carport');
  space('basement');
  space('crawlspace');
  system('structure');
  system('electrical');
  system('plumbing');
  system('waterHeater');
  system('hvac');
  plan.floors.forEach((floorPlan, index) => {
    const floor = index + 1;
    const prefix = plan.floors.length > 1 ? naming.floorPrefix(floor) : '';
    for (const preset of ROOM_PRESETS) {
      instanceNames(naming.roomBase[preset.key], floorPlan.rooms[preset.key]).forEach((name, i, all) => {
        out.push({ kind: preset.kind, name: `${prefix}${name}`, floor, label: { preset: preset.key, index: all.length > 1 ? i + 1 : null, withFloor: plan.floors.length > 1 } });
      });
    }
    for (const extra of floorPlan.extras) {
      const name = extra.trim();
      if (name.length > 0) out.push({ kind: 'interior', name, floor, label: null });
    }
  });
  for (const other of plan.others) {
    const name = other.trim();
    if (name.length > 0) out.push({ kind: 'other', name, floor: null, label: null });
  }
  space('attic');
  system('safety');
  return out;
}

export interface PlanCounts {
  floors: number;
  bedrooms: number;
  // Full baths + 0.5 per half bath, e.g. 2.5.
  baths: number;
  kitchens: number;
  areas: number;
}

export function planCounts(plan: HousePlan): PlanCounts {
  let bedrooms = 0;
  let baths = 0;
  let kitchens = 0;
  for (const floor of plan.floors) {
    bedrooms += floor.rooms.bedroom;
    baths += floor.rooms.fullBath + floor.rooms.halfBath * 0.5;
    kitchens += floor.rooms.kitchen;
  }
  return { floors: plan.floors.length, bedrooms, baths, kitchens, areas: areaSeedsFromPlan(plan).length };
}

// "2 层 · 3 卧 · 2.5 卫 · 1 厨 · 地下室 · 阁楼 · 车库" — the one-line read of the house; '' when
// nothing is set yet (the UI prints 未设置).
export function planSummary(plan: HousePlan): string {
  const c = planCounts(plan);
  const parts: string[] = [];
  if (c.floors > 0) parts.push(`${c.floors} 层`);
  if (c.bedrooms > 0) parts.push(`${c.bedrooms} 卧`);
  if (c.baths > 0) parts.push(`${c.baths} 卫`);
  if (c.kitchens > 0) parts.push(`${c.kitchens} 厨`);
  for (const preset of SPACE_PRESETS) {
    const n = plan.spaces[preset.key];
    if (n <= 0) continue;
    parts.push(n > 1 ? `${preset.label} ×${n}` : preset.label);
  }
  return parts.join(' · ');
}

// "厨房 1 · 客厅 1 · 半卫 1 · 书房" for one floor's disclosure row.
export function floorSummary(floor: FloorPlan): string {
  const parts: string[] = [];
  for (const preset of ROOM_PRESETS) {
    const n = floor.rooms[preset.key];
    if (n > 0) parts.push(`${preset.label} ${n}`);
  }
  parts.push(...floor.extras);
  return parts.length > 0 ? parts.join(' · ') : '还没有房间';
}

// ---------- derivation back from Area records (after creation) ----------

export interface AreaSection {
  order: number;
  label: string;
}

// Where an area sits in the grouped list: floors are derived from Area.floor; everything
// else from its catalog group. Records written before floors existed (floor = null) simply
// land in the non-floor sections — nothing is migrated.
export function areaSectionOf(area: Pick<V2Area, 'kind' | 'floor'>): AreaSection {
  if (area.floor !== null) return { order: 100 + area.floor, label: floorLabel(area.floor) };
  switch (areaDefinition(area.kind).group) {
    case 'exterior':
      return { order: 0, label: '外部' };
    case 'spaces':
      return { order: 10, label: '附属空间' };
    case 'systems':
      return { order: 20, label: '系统' };
    case 'rooms':
      return { order: 99, label: '房间' };
    case 'wholeHouse':
      return { order: 1000, label: '整屋' };
  }
}

export interface AreaSectionGroup<T> {
  section: AreaSection;
  areas: T[];
}

// Inverse of the section order numbers above, for localised group headers (src/i18n/labels.ts).
export type AreaSectionKind = AreaGroup | 'floor';

export function areaSectionKindOf(section: Pick<AreaSection, 'order'>): { kind: AreaSectionKind; floor: number | null } {
  if (section.order >= 100 && section.order < 1000) return { kind: 'floor', floor: section.order - 100 };
  switch (section.order) {
    case 0:
      return { kind: 'exterior', floor: null };
    case 10:
      return { kind: 'spaces', floor: null };
    case 20:
      return { kind: 'systems', floor: null };
    case 99:
      return { kind: 'rooms', floor: null };
    default:
      return { kind: 'wholeHouse', floor: null };
  }
}

// Keeps the incoming order inside each section (callers pass areas sorted by sortOrder).
export function groupAreasBySection<T extends Pick<V2Area, 'kind' | 'floor'>>(areas: readonly T[]): AreaSectionGroup<T>[] {
  const groups = new Map<number, AreaSectionGroup<T>>();
  for (const area of areas) {
    const section = areaSectionOf(area);
    const group = groups.get(section.order);
    if (group) group.areas.push(area);
    else groups.set(section.order, { section, areas: [area] });
  }
  return [...groups.values()].sort((a, b) => a.section.order - b.section.order);
}

export function floorCountOf(areas: readonly Pick<V2Area, 'floor'>[]): number {
  let max = 0;
  for (const area of areas) if (area.floor !== null && area.floor > max) max = area.floor;
  return max;
}

// Stem of a new instance added after creation: the floor prefix when the house has (or is getting)
// more than one floor, then the preset's base name.
function instanceStem(base: string, floor: number | null, areas: readonly Pick<V2Area, 'name' | 'floor'>[], floorPrefix: (floor: number) => string): { stem: string; withFloor: boolean } {
  const floorCount = Math.max(floorCountOf(areas), floor ?? 0);
  const prefix = floor !== null && floorCount > 1 ? floorPrefix(floor) : '';
  return { stem: `${prefix}${base}`, withFloor: prefix.length > 0 };
}

// Name for a new instance added after creation: floor prefix when the house has (or is
// getting) more than one floor, then the next free number in that series.
export function suggestedAreaName(
  base: string,
  floor: number | null,
  areas: readonly Pick<V2Area, 'name' | 'floor'>[],
  floorPrefix: (floor: number) => string = floorLabel
): string {
  return nextInstanceName(instanceStem(base, floor, areas, floorPrefix).stem, areas.map((a) => a.name));
}

// Name AND language-neutral label of a preset instance added after creation (添加区域 with the preset
// left unnamed): the same stem and numbering as suggestedAreaName, and the label that describes it —
// the preset, the number the name ends with (none for the first instance) and whether the floor prefix
// was applied — so src/i18n/labels.ts re-expresses "二楼卧室 4" as "Second Floor Bedroom 4" in the Report
// Language, exactly like a plan-seeded room (task book §3 / §18). Until 2026-10-18 every added area was
// stored inspector-named: the English client report of a zh-CN inspection printed 「卧室」, and since the
// closeout checks for Chinese (d7472fc) it asked the inspector to rename it in English.
export function suggestedAreaInstance(
  preset: string,
  base: string,
  floor: number | null,
  areas: readonly Pick<V2Area, 'name' | 'floor'>[],
  floorPrefix: (floor: number) => string = floorLabel
): { name: string; label: AreaNameLabel } {
  const { stem, withFloor } = instanceStem(base, floor, areas, floorPrefix);
  const name = nextInstanceName(stem, areas.map((a) => a.name));
  const suffix = name.slice(stem.length).trim();
  const index = /^\d+$/.test(suffix) ? Number.parseInt(suffix, 10) : null;
  return { name, label: { preset, index, withFloor } };
}
